import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { FaLocationArrow } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { editData } from '../../redux/actions/api/EditData';
import Breadcrumb from '../../components/breadcrumb/Breadcrumb';
import Loading from '../../components/loading/Loading';
import {
  checkIfFilesAreCorrectType,
  checkIfFilesAreTooBig,
} from '../../utils/function';

export default function EditBlog() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken } = useSelector((state) => state.auth);
  const blog = useSelector((state) =>
    state.api.data.find((blog) => blog.id.toString() === id)
  );
  const { loading, error } = useSelector((state) => state.api);
  const [descriptionAr, setDescriptionAr] = useState(blog?.description_ar);
  const [descriptionEn, setDescriptionEn] = useState(blog?.description_en);
  // ----------------------------------------------------------------------------------->
  useEffect(() => {
    error && toast.error(error);
  }, [dispatch, error, loading]);
  // ----------------------------------------------------------------------------------->
  const initialValues = {
    // initial Values input felids
    image: '',
    title: {
      ar: blog?.title_ar,
      en: blog?.title_en,
    },
  };
  // ----------------------------------------------------------------------------------->
  const validationSchema = Yup.object({
    // validation and check input is not empty
    title: Yup.object().shape({
      ar: Yup.string()
        .required(`title ar required`)
        .min(3, `The title ar you should not be less than 3 characters`)
        .max(70, `The title ar you must not than 70 characters`),
      en: Yup.string()
        .required(`title en required`)
        .min(3, `The title en you should not be less than 3 characters`)
        .max(70, `The title en you must not than 70 characters`),
    }),
    image: Yup.mixed()
      .nullable()
      .test(
        'fileSize',
        'File size too large, max file size is 2 Mb',
        (value) => !value || checkIfFilesAreTooBig(value)
      )
      .test(
        'fileFormat',
        'Uploaded file has unsupported format.',
        (value) => !value || checkIfFilesAreCorrectType(value)
      ),
  });
  // ----------------------------------------------------------------------------------->
  const onSubmit = (values) => {
    // handel to modify blog
    var data = new FormData();
    data.append('image', values?.image || []);
    data.append('title_en', values?.title?.en);
    data.append('title_ar', values?.title?.ar);
    data.append('description_en', descriptionEn);
    data.append('description_ar', descriptionAr);
    data.append('_method', 'PUT');
    dispatch(
      editData(`articles/${id}`, accessToken, data, navigate, '/blogs', true)
    );
  };
  return (
    <>
      <Loading isLoading={loading} />
      <Breadcrumb
        title='Edit a blog'
        textActive='Edit'
        items={[{ title: 'Blogs' }]}
      />
      <div className='card rounded-1 mb-4 p-3'>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {(formik) =>
            formik.values && (
              <Form>
                <div className='row'>
                  <div className='col-md-6'>
                    <div className='form-group'>
                      <label htmlFor='title_ar' className='text-muted'>
                        Enter your title ar <span className='required'>*</span>
                      </label>
                      <input
                        className='form-control'
                        name='title.ar'
                        id='title_ar'
                        inputMode='text'
                        type='text'
                        {...formik.getFieldProps('title.ar')}
                        placeholder='write title here....'
                      />
                      {formik.touched.title?.ar && formik.errors.title?.ar ? (
                        <div className='text-error'>
                          {formik.errors.title?.ar}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='form-group'>
                      <label htmlFor='title_en' className='text-muted'>
                        Enter your title en <span className='required'>*</span>
                      </label>
                      <input
                        className='form-control'
                        name='title.en'
                        id='title_en'
                        inputMode='text'
                        type='text'
                        placeholder='write title here....'
                        {...formik.getFieldProps('title.en')}
                      />
                      {formik.touched.title?.en && formik.errors.title?.en ? (
                        <div className='text-error'>
                          {formik.errors.title?.en}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='form-group'>
                      <label htmlFor='description_ar' className='text-muted'>
                        Enter your description ar{' '}
                        <span className='required'>*</span>
                      </label>
                      <CKEditor
                        key='description.ar'
                        editor={ClassicEditor}
                        data={descriptionAr}
                        onChange={(event, editor) => {
                          const data = editor?.getData();
                          setDescriptionAr(data);
                        }}
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='form-group'>
                      <label htmlFor='description_en' className='text-muted'>
                        Enter your description en{' '}
                        <span className='required'>*</span>
                      </label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={descriptionEn}
                        onChange={(event, editor) => {
                          const data = editor?.getData();
                          setDescriptionEn(data);
                        }}
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='form-group'>
                      <label htmlFor='image' className='text-muted'>
                        Enter your image <span className='required'>*</span>
                      </label>
                      <input
                        className='form-control'
                        name='image'
                        accept='image/jpeg,image/png,image/svg'
                        id='image'
                        onChange={(e) =>
                          formik.setFieldValue('image', e.target.files[0])
                        }
                        type='file'
                      />
                      {formik.touched.image && formik.errors.image ? (
                        <div className='text-error'>{formik.errors.image}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className='col-md-12 mt-3 text-center'>
                    <button
                      className='btn btn-primary btn-user btn-inline'
                      type='submit'
                    >
                      <FaLocationArrow style={{ marginRight: '.5rem' }} /> Edit
                    </button>
                  </div>
                </div>
              </Form>
            )
          }
        </Formik>
      </div>
    </>
  );
}
